import React from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import NotFound from "../pages/NotFound";
import LoginPage from "../pages/login/page";
import UnifiedDashboard from "../pages/dashboard/page";
import CommandeNouvellePage from "../pages/commande-nouvelle/page";
import VenteTissu from "../pages/vente-tissu/page";
import GestionStocks from "../pages/gestion-stocks/page";
import PaiePersonnel from "../pages/paie-personnel/page";
import GestionUtilisateurs from "../pages/gestion-utilisateurs/page";
import CommandeDetail from "../pages/commande-detail/page";
import StocksNouveau from "../pages/stocks-nouveau/page";
import DashboardSuperAdmin from "../pages/dashboard-superadmin/page";
import GestionMesures from "../pages/gestion-mesures/page";
import ParametresMesures from "../pages/parametres-mesures/page";
import CatalogueModeles from "../pages/catalogue-modeles/page";
import ParametresPersonnel from "../pages/parametres-personnel/page";
import Rapports from "../pages/rapports/page";
import HistoriqueClients from "../pages/historique-clients/page";
import ParametresEntreprise from "../pages/parametres-entreprise/page";
import ProductionPage from "../pages/production/page";
import FournisseurPage from "../pages/fournisseurs/page";
import FinancesPage from "../pages/finances/page";

import ParametresHabitTypes from "../pages/parametres-habit-types/page";
import HistoriqueActivites from "../pages/parametres-audit/page";
import { checkPermission } from "../utils/permissions";

interface ProtectedRouteProps {
  element: React.ReactElement;
  permission: string;
}

const ProtectedRoute = ({ element, permission }: ProtectedRouteProps) => {
  const userStr = localStorage.getItem('atelier_bassoum_user');
  
  if (!userStr) {
    return <Navigate to="/login" replace />;
  }
  
  const hasPerm = checkPermission(permission);
  
  if (!hasPerm) {
    sessionStorage.setItem('auth_error', `Accès refusé. Vous n'avez pas la permission requise pour cette page (${permission}).`);
    return <Navigate to="/dashboard" replace />;
  }
  
  return element;
};

const ProtectedSuperAdmin = ({ element }: { element: React.ReactElement }) => {
  const userStr = localStorage.getItem('atelier_bassoum_user');
  if (!userStr) return <Navigate to="/login" replace />;
  
  try {
    const user = JSON.parse(userStr);
    if (user.role !== 'superadmin') {
      sessionStorage.setItem('auth_error', "Accès refusé. Réservé au super-administrateur.");
      return <Navigate to="/dashboard" replace />;
    }
  } catch (e) {
    return <Navigate to="/login" replace />;
  }
  
  return element;
};

const routes: RouteObject[] = [
  { path: "/", element: <Navigate to="/login" replace /> },
  { path: "/login", element: <LoginPage /> },
  { path: "/dashboard", element: <UnifiedDashboard /> },
  { path: "/dashboard/vendeur", element: <Navigate to="/dashboard" replace /> },
  { path: "/dashboard/gerant", element: <Navigate to="/dashboard" replace /> },
  { path: "/dashboard/atelier", element: <Navigate to="/dashboard" replace /> },
  { path: "/dashboard/coupeur", element: <Navigate to="/dashboard" replace /> },
  { path: "/dashboard/tailleur", element: <Navigate to="/dashboard" replace /> },
  { path: "/dashboard/superadmin", element: <ProtectedSuperAdmin element={<DashboardSuperAdmin />} /> },
  { path: "/commande/nouvelle", element: <ProtectedRoute element={<CommandeNouvellePage />} permission="cmd_creer" /> },
  { path: "/commande/:id", element: <ProtectedRoute element={<CommandeDetail />} permission="cmd_voir" /> },
  { path: "/vente-tissu", element: <ProtectedRoute element={<VenteTissu />} permission="vente_creer" /> },
  { path: "/stocks", element: <ProtectedRoute element={<GestionStocks />} permission="stock_voir" /> },
  { path: "/stocks/nouveau", element: <ProtectedRoute element={<StocksNouveau />} permission="stock_voir" /> },
  { path: "/paie", element: <ProtectedRoute element={<PaiePersonnel />} permission="paie_voir" /> },
  { path: "/finances", element: <ProtectedRoute element={<FinancesPage />} permission="ca_voir" /> },
  { path: "/fournisseurs", element: <ProtectedRoute element={<FournisseurPage />} permission="fourn_voir" /> },
  { path: "/utilisateurs", element: <ProtectedRoute element={<GestionUtilisateurs />} permission="user_gerer" /> },
  { path: "/mesures", element: <ProtectedRoute element={<GestionMesures />} permission="mesure_voir" /> },
  { path: "/rapports", element: <ProtectedRoute element={<Rapports />} permission="rapport_personnel" /> },
  { path: "/catalogue-modeles", element: <ProtectedRoute element={<CatalogueModeles />} permission="stock_modifier" /> },
  { path: "/parametres/mesures", element: <ProtectedRoute element={<ParametresMesures />} permission="config_mesures" /> },
  { path: "/parametres/habit-types", element: <ProtectedRoute element={<ParametresHabitTypes />} permission="stock_modifier" /> },
  { path: "/parametres/personnel", element: <ProtectedRoute element={<ParametresPersonnel />} permission="user_voir" /> },
  { path: "/parametres/audit", element: <ProtectedRoute element={<HistoriqueActivites />} permission="user_gerer" /> },
  { path: "/clients/historique", element: <ProtectedRoute element={<HistoriqueClients />} permission="client_voir" /> },
  { path: "/parametres/entreprise", element: <ProtectedRoute element={<ParametresEntreprise />} permission="config_entreprise" /> },
  { path: "/production", element: <ProtectedRoute element={<ProductionPage />} permission="dash_atelier" /> },
  { path: "*", element: <NotFound /> },
];
export default routes;
